import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useCreateCurrentAssessmentMutation } from "../../infrastructure/api/patientsApi";

/**
 * Stop reasons. `apiValue` is what's stored as the cancellation reason, kept in
 * English so records read the same whatever language the user has selected.
 */
const STOP_REASONS = [
  { key: "appReinstalled", apiValue: "App reinstalled" },
  { key: "connectionIssue", apiValue: "Connection issue" },
  { key: "deviceIssue", apiValue: "Device/technical issue" },
  { key: "other", apiValue: "Other" },
] as const;

type StopReasonKey = (typeof STOP_REASONS)[number]["key"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the screening has been stopped so the parent can refetch. */
  onSuccess?: () => void;
  patientId: string;
}

export const StopScreeningDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onSuccess,
  patientId,
}) => {
  const { t } = useTranslation("patients");
  const [reasonKey, setReasonKey] = useState<StopReasonKey | "">("");
  const [otherReason, setOtherReason] = useState("");
  const [stopScreening, { isLoading }] = useCreateCurrentAssessmentMutation();

  const needsOtherText = reasonKey === "other";
  const canStop = !!reasonKey && (!needsOtherText || !!otherReason.trim());

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setReasonKey("");
      setOtherReason("");
    }
    onOpenChange(next);
  };

  const handleStop = async () => {
    const reason = STOP_REASONS.find((r) => r.key === reasonKey);
    if (!reason || !canStop) return;

    try {
      await stopScreening({
        patientId,
        data: {
          status: "SCR_CANCELLED",
          reason: needsOtherText
            ? `${reason.apiValue}: ${otherReason.trim()}`
            : reason.apiValue,
        },
      }).unwrap();
      toast.success(t("stopScreeningDialog.success"));
      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data
        ?.message;
      toast.error(message || t("stopScreeningDialog.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <TriangleAlert className="h-5 w-5 shrink-0 text-[#E53935]" />
            {t("stopScreeningDialog.title")}
          </DialogTitle>
          <p className="mt-1 text-sm text-gray-500">
            {t("stopScreeningDialog.body")}
          </p>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-900">
            {t("stopScreeningDialog.reasonLabel")}{" "}
            <span className="text-destructive-text">*</span>
          </p>
          <RadioGroup
            value={reasonKey}
            onValueChange={(value) => setReasonKey(value as StopReasonKey)}
            disabled={isLoading}
            className="gap-3"
          >
            {STOP_REASONS.map((reason) => (
              <div key={reason.key} className="flex items-center gap-2">
                <RadioGroupItem value={reason.key} id={`stop-reason-${reason.key}`} />
                <Label
                  htmlFor={`stop-reason-${reason.key}`}
                  className="cursor-pointer text-sm font-normal text-[#1B1D21]"
                >
                  {t(`stopScreeningDialog.reasons.${reason.key}`)}
                </Label>
              </div>
            ))}
          </RadioGroup>

          {needsOtherText && (
            <Textarea
              placeholder={t("stopScreeningDialog.otherPlaceholder")}
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              disabled={isLoading}
              maxLength={250}
              autoFocus
            />
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
            className="px-4 py-5 text-primary border-primary hover:text-primary hover:bg-white cursor-pointer"
          >
            {t("stopScreeningDialog.cancel")}
          </Button>
          <Button
            onClick={() => void handleStop()}
            disabled={!canStop}
            loading={isLoading}
            className="bg-[#E53935] hover:bg-[#E53935] text-white px-4 py-5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("stopScreeningDialog.stop")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
